import { cn } from "@portal/components/lib/utils";
import { PartialIndicator } from "@portal/components/primitives";
import { RequiredForApprovalIndicator } from "@portal/components/ui/RequiredForApprovalIndicator";
import { Tooltip } from "@portal/components/ui/Tooltip";
import { useFormSchema } from "@portal/providers/FormSchemaProvider";
import { ReactNode, useMemo } from "react";
import { FieldValues, Path, UseFormReturn, useWatch } from "react-hook-form";
import { z } from "zod";
import { isPartialField, isRequiredForApprovalField } from "../../utils/form";
import { type ViewSectionFieldSize, viewSectionFieldWidth } from "./viewSectionFieldSize";

/**
 * The one label style for every row in a view-section card. Exported so non-row content in the
 * same card (e.g. the description blockquote) labels itself identically instead of drifting.
 */
export const VIEW_SECTION_ITEM_LABEL_CLASS = "text-primary text-[14px] font-medium";

/** The row stack every view-section card body uses — one column, hairline between rows. */
export const VIEW_SECTION_CARD_STACK_CLASS =
	"flex flex-col divide-y divide-bc-default divide-solid";

export const ViewSectionCardEditItem = <TFieldValues extends FieldValues>({
	label,
	subLabel,
	editing,
	partialActive = true,
	viewComponent,
	editComponent,
	formMethods,
	name,
	schema,
	tooltip,
	fieldSize = "full",
	editComponentClassName,
	iconClassName,
	partialRequiresSubmit = true,
	requiredForApprovalOverride,
	align = "center",
}: {
	label: string;
	subLabel?: string;
	editing: boolean;
	partialActive?: boolean;
	viewComponent: ReactNode;
	editComponent: ReactNode;
	// biome-ignore lint/suspicious/noExplicitAny: legacy any
	formMethods: UseFormReturn<TFieldValues, any, any>;
	name: Path<TFieldValues> | Path<TFieldValues>[];
	// biome-ignore lint/suspicious/noExplicitAny: legacy any
	schema?: z.ZodObject<any>;
	tooltip?: string;
	// Width step for the value/edit slot. Prefer this over hand-rolled max-widths.
	fieldSize?: ViewSectionFieldSize;
	// Escape hatch appended after `fieldSize`, for the rare row the ramp doesn't cover.
	editComponentClassName?: string;
	iconClassName?: string;
	partialRequiresSubmit?: boolean;
	// Force the indicator on/off when "missing" isn't derivable from emptiness (e.g. a $0 bond amount).
	requiredForApprovalOverride?: boolean;
	// "start" for multi-line content (e.g. a textarea) that shouldn't vertically center against the label.
	align?: "center" | "start";
}) => {
	const schemaFromContext = useFormSchema();
	const effectiveSchema = schema ?? schemaFromContext;

	// Both indicators read current values through getValues, which does not subscribe to
	// changes. Watch the field(s) so the indicator clears as soon as a value is entered.
	// Rebuilt from a joined key so an inline `name={[...]}` does not resubscribe every render.
	const nameKey = (Array.isArray(name) ? name : [name]).join("|");
	const watchedNames = useMemo(() => nameKey.split("|") as Path<TFieldValues>[], [nameKey]);
	useWatch({ control: formMethods.control, name: watchedNames });

	const showRequiredForApproval =
		requiredForApprovalOverride ??
		(Array.isArray(name)
			? name.some((n) =>
					isRequiredForApprovalField(formMethods, n, effectiveSchema, partialRequiresSubmit),
				)
			: isRequiredForApprovalField(formMethods, name, effectiveSchema, partialRequiresSubmit));

	const fieldWidthClass = cn(viewSectionFieldWidth(fieldSize), editComponentClassName);

	return (
		<div
			className={cn(
				"relative flex space-x-[24px] justify-between px-[10px]",
				editing ? "py-[7px]" : "py-[14px]",
				align === "start" ? "items-start" : "items-center",
			)}
		>
			<div className="flex-col space-y-[3px] flex">
				<div className={cn(VIEW_SECTION_ITEM_LABEL_CLASS, "flex items-center")}>
					{label}
					{tooltip && (
						<Tooltip id={`tooltip-${name}`} content={tooltip} className="ml-[5px] text-[16px]" />
					)}
				</div>
				{subLabel && <div className="text-tertiary text-[11px] font-[450] italic">{subLabel}</div>}
			</div>
			<span className={cn("text-primary text-[15px] flex min-w-0", fieldWidthClass)}>
				<div
					className={cn("flex gap-x-[8px] align-items-top justify-end min-w-0", fieldWidthClass)}
				>
					{editing ? editComponent : viewComponent}
				</div>
			</span>
			<div
				className={cn(
					"flex items-center h-full absolute top-0 right-[-15px] mt-[1px]",
					iconClassName,
				)}
			>
				{partialActive
					? Array.isArray(name)
						? name
								.map((n) => isPartialField(formMethods, n, effectiveSchema, partialRequiresSubmit))
								.some(Boolean) && (
								<PartialIndicator active={partialActive} testId="partial-indicator" />
							)
						: isPartialField(formMethods, name, effectiveSchema, partialRequiresSubmit) && (
								<PartialIndicator active={partialActive} testId="partial-indicator" />
							)
					: null}
				{showRequiredForApproval && <RequiredForApprovalIndicator active className="mr-[-1px]" />}
			</div>
		</div>
	);
};
